/**
 * Control Portal - Shipment & Trip Management
 * Platform-wide view of all shipments across all customers
 */
import { useState, useEffect, useCallback } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Package, Search, RefreshCw, Loader2, Clock, CheckCircle,
  AlertTriangle, Building2, Truck, Thermometer, MapPin, Eye,
  FileText, Calendar
} from 'lucide-react';

export default function ShipmentsPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [customerStats, setCustomerStats] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, shipment: null, loading: false });
  const [shipmentDetail, setShipmentDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tenantFilter !== 'all') params.tenant_id = tenantFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [summaryRes, shipmentsRes, customersRes, tenantsRes] = await Promise.all([
        controlApi.getShipmentSummary(params),
        controlApi.getShipments(params),
        controlApi.getShipmentsByCustomer(),
        controlApi.getTenants()
      ]);
      
      setSummary(summaryRes.data);
      setShipments(shipmentsRes.data);
      setCustomerStats(customersRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error('Failed to load shipment data');
    } finally {
      setLoading(false);
    }
  }, [tenantFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openShipmentDetail = async (shipment) => {
    setDetailDialog({ open: true, shipment, loading: true });
    try {
      const res = await controlApi.getShipmentDetail(shipment.id);
      setShipmentDetail(res.data);
    } catch (error) {
      toast.error('Failed to load shipment details');
    } finally {
      setDetailDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const getStatusBadge = (status, isDelayed) => {
    if (isDelayed) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">delayed</Badge>;
    }
    const styles = {
      planned: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return <Badge className={styles[status] || styles.planned}>{status?.replace('_', ' ')}</Badge>;
  };

  const filteredShipments = shipments.filter(s => 
    s.trip_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.tenant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.driver_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="shipments-title">
            <Package className="w-7 h-7 text-blue-500" />
            Shipment & Trip Management
          </h1>
          <p className="text-slate-400 mt-1">Monitor all shipments across all customers</p>
        </div>
        <Button 
          onClick={fetchData} 
          variant="outline"
          className="border-slate-700 text-slate-300"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="shipment-summary">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400 text-sm">Total Trips</span>
            </div>
            <p className="text-2xl font-bold text-white">{summary.total_trips}</p>
          </div>
          
          <div className="bg-slate-900 rounded-xl border border-blue-500/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5 text-blue-400" />
              <span className="text-slate-400 text-sm">Active</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{summary.active_trips}</p>
          </div>
          
          <div className="bg-slate-900 rounded-xl border border-red-500/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-slate-400 text-sm">Delayed</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{summary.delayed_trips}</p>
          </div>
          
          <div className="bg-slate-900 rounded-xl border border-green-500/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-slate-400 text-sm">Completed Today</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{summary.completed_today}</p>
          </div>
          
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-green-400" />
              <span className="text-slate-400 text-sm">On-Time Rate</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{summary.on_time_rate}%</p>
          </div>
        </div>
      )}

      {/* Customer Summary */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
        <h3 className="text-white font-medium mb-3">By Customer</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {customerStats.slice(0, 6).map((c) => (
            <button
              key={c.tenant_id}
              onClick={() => setTenantFilter(c.tenant_id)}
              className={`p-3 rounded-lg border text-left transition-all ${
                tenantFilter === c.tenant_id 
                  ? 'border-blue-500/50 bg-blue-500/10' 
                  : 'border-slate-700 hover:border-slate-600'
              }`}
            >
              <p className="text-white font-medium text-sm truncate">{c.name}</p>
              <div className="flex gap-2 mt-1 text-xs">
                <span className="text-blue-400">{c.active} active</span>
                {c.delayed > 0 && <span className="text-red-400">{c.delayed} delayed</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search by trip number, customer, driver..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="planned" className="text-white">Planned</SelectItem>
            <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
            <SelectItem value="completed" className="text-white">Completed</SelectItem>
            <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-white">All Customers</SelectItem>
            {tenants.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Shipments Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Trip #</TableHead>
              <TableHead className="text-slate-400">Customer</TableHead>
              <TableHead className="text-slate-400">Vehicle / Driver</TableHead>
              <TableHead className="text-slate-400">Device</TableHead>
              <TableHead className="text-slate-400">Temp Range</TableHead>
              <TableHead className="text-slate-400">Invoices</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredShipments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No shipments found
                </TableCell>
              </TableRow>
            ) : (
              filteredShipments.map((s) => (
                <TableRow key={s.id} className="border-slate-800">
                  <TableCell>
                    <p className="text-white font-mono">{s.trip_number}</p>
                    {s.planned_start && (
                      <p className="text-slate-500 text-xs">
                        {new Date(s.planned_start).toLocaleDateString()}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{s.tenant_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-white">{s.vehicle_number || '-'}</p>
                    <p className="text-slate-500 text-xs">{s.driver_name || 'No driver'}</p>
                  </TableCell>
                  <TableCell className="text-slate-300 font-mono text-sm">
                    {s.device_uid || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Thermometer className="w-4 h-4 text-cyan-400" />
                      <span className="text-slate-300">{s.temp_min}°C - {s.temp_max}°C</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-slate-600">
                      <FileText className="w-3 h-3 mr-1" />
                      {s.invoice_count}
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(s.status, s.is_delayed)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-white"
                      onClick={() => openShipmentDetail(s)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Shipment Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, shipment: null, loading: false })}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-400" />
              Shipment Details
            </DialogTitle>
          </DialogHeader>
          
          {detailDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : shipmentDetail && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-mono text-lg">{shipmentDetail.trip_number}</p>
                    <p className="text-slate-400">{shipmentDetail.tenant_name}</p>
                  </div>
                  {getStatusBadge(shipmentDetail.status, shipmentDetail.is_delayed)}
                </div>
              </div>
              
              {/* Trip Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-slate-400 text-xs uppercase">Vehicle</p>
                  <p className="text-white">{shipmentDetail.vehicle_number || '-'}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-slate-400 text-xs uppercase">Driver</p>
                  <p className="text-white">{shipmentDetail.driver_name || '-'}</p>
                  {shipmentDetail.driver_phone && (
                    <p className="text-slate-500 text-xs">{shipmentDetail.driver_phone}</p>
                  )}
                </div>
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-slate-400 text-xs uppercase">Device</p>
                  <p className="text-white font-mono">{shipmentDetail.device_uid || '-'}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-slate-400 text-xs uppercase">Temp Range</p>
                  <p className="text-cyan-400">{shipmentDetail.temp_min}°C - {shipmentDetail.temp_max}°C</p>
                </div>
              </div>
              
              {/* Temperature Stats */}
              {shipmentDetail.temperature_stats && shipmentDetail.temperature_stats.readings_count > 0 && (
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-xs uppercase mb-3">Temperature Stats</p>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <p className="text-slate-500 text-xs">Min</p>
                      <p className="text-white">{shipmentDetail.temperature_stats.min}°C</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Max</p>
                      <p className="text-white">{shipmentDetail.temperature_stats.max}°C</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Avg</p>
                      <p className="text-white">{shipmentDetail.temperature_stats.avg}°C</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Breaches</p>
                      <p className={shipmentDetail.temperature_stats.breaches > 0 ? 'text-red-400' : 'text-green-400'}>
                        {shipmentDetail.temperature_stats.breaches}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Timeline */}
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase mb-3">Timeline</p>
                <div className="space-y-2 text-sm">
                  {shipmentDetail.planned_start && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Planned Start</span>
                      <span className="text-white">{new Date(shipmentDetail.planned_start).toLocaleString()}</span>
                    </div>
                  )}
                  {shipmentDetail.actual_start && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Actual Start</span>
                      <span className="text-green-400">{new Date(shipmentDetail.actual_start).toLocaleString()}</span>
                    </div>
                  )}
                  {shipmentDetail.planned_end && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Planned End</span>
                      <span className="text-white">{new Date(shipmentDetail.planned_end).toLocaleString()}</span>
                    </div>
                  )}
                  {shipmentDetail.actual_end && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Actual End</span>
                      <span className="text-green-400">{new Date(shipmentDetail.actual_end).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Invoices */}
              {shipmentDetail.invoices && shipmentDetail.invoices.length > 0 && (
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-xs uppercase mb-3">Invoices ({shipmentDetail.invoices.length})</p>
                  <div className="space-y-2">
                    {shipmentDetail.invoices.map((inv, idx) => (
                      <div key={inv.id} className="flex justify-between items-center py-2 border-b border-slate-700 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-xs">#{inv.sequence || idx + 1}</span>
                          <span className="text-white">{inv.invoice_id?.slice(0, 8)}...</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {inv.verified && <CheckCircle className="w-4 h-4 text-green-400" />}
                          {getStatusBadge(inv.status, false)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
